import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/ApiError';

/**
 * Server state, held by TanStack Query rather than in components.
 *
 * <h2>Why a query library and not `useEffect` with `useState`</h2>
 *
 * Data that lives on a server is not component state. Treating it as such means
 * every screen reimplements the same four things — loading, error, caching and
 * refetching — and the reimplementations disagree. It is also where the request
 * waterfall comes from: each component fetching in its own effect, one after
 * another, because nothing coordinates them.
 *
 * <p>Separated from `QueryProvider` so a test can build the client the application actually
 * runs on. `postLogin.test.ts` built a bare `new QueryClient()` instead, inherited the
 * library's five-minute `gcTime`, and went on asserting a saving this application had
 * stopped making.
 */
export function buildClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /*
         * NOTHING IS CACHED IN THE BROWSER. Every screen asks the server.
         *
         * `staleTime: 0` makes every mount a request; `gcTime: 0` throws the answer away
         * the moment nothing is rendering it, so a second visit starts from nothing rather
         * than painting an old answer while it re-asks.
         *
         * <p>It replaced a per-hook staleTime -- a minute here, ten minutes on the
         * company's allowances -- and the ten-minute one is why this was decided. How long
         * data stays good is the server's to know: an operator withdrawing a product was
         * invisible for ten minutes, the form went on offering it, and the person found out
         * from a rejected submit. A number in a React hook cannot know that, so it does not
         * get to guess.
         *
         * <p>The cost is stated rather than hidden: a loader on every visit to every
         * screen, and a request the server would often answer with the same bytes. Caching
         * belongs on the server, where eviction can be tied to the writes that change the
         * answer -- provisioning, a plan change, an operator's edit. Until that exists,
         * correct and slower beats fast and wrong.
         *
         * <p>Set HERE and not per hook, so it is one decision in one place. Per hook, a new
         * query opts back into caching by forgetting, and nothing says it did.
         */
        staleTime: 0,
        gcTime: 0,

        // Off. Only a navigation asks again. It fires on every tab switch otherwise and,
        // on a dashboard of a dozen queries, turns alt-tabbing into a burst of requests
        // nobody asked for.
        refetchOnWindowFocus: false,

        retry: (failureCount, error) => {
          // Retrying a 400 or a 403 asks the same question and gets the same
          // answer, three times as slowly. Only a network failure or a 5xx has
          // any chance of a different outcome.
          if (error instanceof ApiError && !error.isRetryable) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        // Never. A mutation is not safe to repeat by default -- a retried sign-up
        // is a second account, and a retried activation spends a token twice.
        retry: false,
      },
    },
  });
}
